import { call, put, select } from 'redux-saga/effects';
import { InvoicesService } from '../../../services/Invoices';
import {
  Types,
  IInvoicesResponse,
  IInvoiceCreditsResponse,
  IInvoiceData,
  IInvoiceBilletResponse,
  InvoicesState,
  IInvoiceXMLResponse
} from './types';
import {
  loadDuplicatesSuccess,
  loadDuplicatesFailure,
  loadInvoiceCreditsSuccess,
  loadInvoiceCreditsFailure,
  loadNFFileFromFinancialsSuccess,
  loadNFFileFromFinancialsFailure,
  loadInvoiceBilletSuccess,
  loadInvoiceBilletFailure,
  loadInvoiceCredits,
  filterDuplicatesByDateSuccess,
  loadOldInvoices,
  loadInvoiceXmlSuccess,
  loadInvoiceXmlFailure
} from './actions';
import { CustomersService } from '../../../services/Customers';
import { ICustomer } from '../customers/types';
import { loadCustomerFromIdSuccess } from '../customers/actions';
import { ECustomerTypeView } from '../user/types';
import { IDownloadNFEResponse } from '../long-steel-orders/types';
import { loadCreditLimits } from '../credit-limits/actions';
import { ApplicationState } from '../..';
import moment from 'moment';
import { DownloadAreaService } from '../../../services/DownloadArea';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { AxiosError } from 'axios';

interface ILoadDuplicatesParams {
  payload: {
    customerId: string;
    customerViewType: ECustomerTypeView;
    startDate: moment.Moment;
    endDate: moment.Moment;
    needToLoadCustomer: boolean;
    selectedStatus: string;
    isFacelift: boolean;
  };
  type: Types;
}

interface ILoadCreditsParams {
  payload: {
    customerId: string;
  };
  type: Types;
}

interface ILoadNFParams {
  payload: {
    invoiceItem: IInvoiceData;
    customerId: string;
    customerViewType: ECustomerTypeView;
  };
  type: Types;
}

interface IFilterDuplicatesByDate {
  payload: {
    startDate: moment.Moment;
    endDate: moment.Moment;
  };
  type: Types;
}

export function* loadInvoicesDuplicatesSaga(params: ILoadDuplicatesParams) {
  const {
    customerId,
    customerViewType,
    startDate,
    endDate,
    needToLoadCustomer,
    selectedStatus
  } = params.payload;

  if (needToLoadCustomer) {
    try {
      const customerService = new CustomersService();
      const customer: ICustomer = yield customerService.getCustomerById(
        customerId
      );
      yield put(loadCustomerFromIdSuccess(customer));
    } catch (err) {
      console.error('loadInvoicesDuplicatesSaga', err);
      yield put(loadDuplicatesFailure(err, true));
      return;
    }
  }

  try {
    const invoicesService = new InvoicesService();

    yield put(loadCreditLimits(customerId));
    if (customerViewType === ECustomerTypeView.EMITTER) {
      yield put(loadInvoiceCredits(customerId));
    }
    const formatedStartDate = startDate ? startDate.format('YYYY-MM-DD') : '';
    const formatedEndDate = endDate ? endDate.format('YYYY-MM-DD') : '';

    const invoicesResponse: IInvoicesResponse =
      yield invoicesService.getDuplicates(
        customerId,
        customerViewType,
        formatedStartDate,
        formatedEndDate,
        selectedStatus
      );

    yield put(loadOldInvoices([]));
    yield put(loadDuplicatesSuccess(invoicesResponse));
  } catch (err) {
    console.error('loadInvoicesDuplicatesSaga', err);
    yield put(
      addToast({
        message: 'Falha ao carregar as Duplicatas.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadDuplicatesFailure(err));
  }
}

export function* filterDuplicatesByDateSaga(params: IFilterDuplicatesByDate) {
  const { startDate, endDate } = params.payload;
  const invoices: InvoicesState = yield select(
    (state: ApplicationState) => state.invoices
  );
  let filteredInvoices: IInvoiceData[] = [];

  if (invoices.oldInvoices?.length) {
    filteredInvoices = invoices.oldInvoices.filter((invoice) => {
      return moment(invoice.dueDate).isBetween(startDate, endDate);
    });
  } else if (invoices.invoices) {
    filteredInvoices = invoices.invoices.filter((invoice) => {
      return moment(invoice.dueDate).isBetween(startDate, endDate);
    });

    yield put(loadOldInvoices(invoices.invoices));
  }

  yield put(filterDuplicatesByDateSuccess(filteredInvoices));
}

export function* loadInvoiceCreditsSaga(params: ILoadCreditsParams) {
  const { customerId } = params.payload;
  try {
    const invoiceService = new InvoicesService();
    const invoiceCredits: IInvoiceCreditsResponse = yield call(
      invoiceService.getInvoiceCredits.bind(invoiceService),
      customerId
    );
    yield put(loadInvoiceCreditsSuccess(invoiceCredits));
  } catch (err) {
    console.error('loadInvoiceCredits', err);
    yield put(
      addToast({
        message: 'Falha ao carregar os Créditos a compensar.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadInvoiceCreditsFailure(err));
  }
}

export function* loadNFFileFromFinancialsSaga(params: ILoadNFParams) {
  const { invoiceItem, customerId, customerViewType } = params.payload;
  try {
    const invoicesService = new InvoicesService();
    const downloadNFEResponse: IDownloadNFEResponse = yield call(
      invoicesService.loadNFEFileFromFinancials.bind(invoicesService),
      customerId,
      invoiceItem,
      customerViewType
    );

    yield put(
      loadNFFileFromFinancialsSuccess(invoiceItem, downloadNFEResponse)
    );
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(
      addToast({
        message: 'Falha ao baixar a Nota fiscal.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadNFFileFromFinancialsFailure(invoiceItem, err));
  }
}

export function* loadInvoiceBilletSaga(params: ILoadNFParams) {
  const { invoiceItem } = params.payload;
  try {
    const invoicesService = new InvoicesService();
    const {
      taxNumberAccount,
      dueDate,
      customerCnpj,
      bankSlipNumber,
      bankCode,
      invoiceType
    } = invoiceItem;

    const invoiceBilletResponse: IInvoiceBilletResponse = yield call(
      invoicesService.getBilletInvoice.bind(invoicesService),
      taxNumberAccount,
      dueDate,
      customerCnpj,
      bankSlipNumber,
      bankCode,
      invoiceType
    );

    yield put(loadInvoiceBilletSuccess(invoiceItem, invoiceBilletResponse));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(
      addToast({
        message: 'Falha ao baixar o boleto.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadInvoiceBilletFailure(invoiceItem, err));
  }
}

export function* loadInvoiceXMLSaga(params: ILoadNFParams) {
  const { invoiceItem } = params.payload;

  try {
    const ordersService = new DownloadAreaService();

    const downloadXmlResponse: IInvoiceXMLResponse = yield call(
      ordersService.loadNFXMLDownloadFile.bind(ordersService),
      invoiceItem.billingNumber
    );
    yield put(loadInvoiceXmlSuccess(invoiceItem, downloadXmlResponse));
  } catch (err) {
    yield put(
      addToast({
        message:
          'Erro inesperado, tente novamente mais tarde. Se o erro persistir, procure o seu vendedor.',
        type: EToastTypes.DANGER
      })
    );
    yield put(loadInvoiceXmlFailure(invoiceItem, err as AxiosError));
  }
}
